import { useEffect, useRef } from 'react'
import { startHeroAnimation } from './heroAnimation'

/**
 * Hero. The canvas animation is a progressive enhancement:
 * - mobile and prefers-reduced-motion get a static, CSS-only treatment and the
 *   canvas loop (and GSAP) are never loaded or started;
 * - on desktop the loop pauses offscreen/hidden and every resource is cleaned
 *   up on unmount (see heroAnimation.ts for the lifecycle contract);
 * - GSAP/ScrollTrigger are dynamically imported only on the animated desktop
 *   path, keeping them out of the initial bundle for mobile users.
 */
export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollIndicatorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const section = sectionRef.current
    const canvas = canvasRef.current
    if (!section || !canvas) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const mobile = window.innerWidth < 768

    // Static / CSS-only treatment: no canvas loop, no GSAP download, no
    // entrance tween — content and CTAs are simply visible.
    if (mobile || reducedMotion) {
      section.classList.add('hero-static')
      canvas.style.display = 'none'
      return () => {
        section.classList.remove('hero-static')
      }
    }

    const stop = startHeroAnimation({
      canvas,
      section,
      isMobile: () => false,
      isReducedMotion: () => false,
    })

    // Lazy extras: GSAP pin/scrub + one-shot entrance tween. Loaded only on
    // the animated desktop path so mobile never parses GSAP.
    let cancelled = false
    let cleanupGsap: (() => void) | undefined
    const hideIndicator = () => {
      if (window.scrollY > 100 && scrollIndicatorRef.current) {
        scrollIndicatorRef.current.style.opacity = '0'
        window.removeEventListener('scroll', hideIndicator)
      }
    }
    window.addEventListener('scroll', hideIndicator, { passive: true })

    Promise.all([import('gsap'), import('gsap/ScrollTrigger')])
      .then(([{ gsap }, { ScrollTrigger }]) => {
        if (cancelled) return
        gsap.registerPlugin(ScrollTrigger)
        const st = ScrollTrigger.create({
          trigger: section,
          start: 'top top',
          end: '+=200vh',
          pin: true,
          scrub: true,
        })
        const heroElements = contentRef.current?.querySelectorAll('.hero-animate')
        let tween: gsap.core.Tween | undefined
        if (heroElements && heroElements.length > 0) {
          tween = gsap.from(heroElements, {
            y: 30,
            opacity: 0,
            duration: 1,
            stagger: 0.2,
            delay: 0.3,
            ease: 'power3.out',
          })
        }
        cleanupGsap = () => {
          st.kill()
          tween?.kill()
        }
      })
      .catch(() => {
        // GSAP is decorative; the hero remains fully usable without it.
      })

    return () => {
      cancelled = true
      stop?.()
      cleanupGsap?.()
      window.removeEventListener('scroll', hideIndicator)
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative w-full min-h-[100dvh] overflow-hidden flex items-center justify-center"
      style={{ background: '#0d1117' }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      <div
        ref={contentRef}
        className="relative z-10 text-center px-6 max-w-[800px] mx-auto"
      >
        <h1 className="hero-animate text-[clamp(36px,5vw,64px)] font-bold leading-[1.1] tracking-[-0.02em] text-white">
          Every government bid in New England in your dashboard
          <br />
          <span className="text-gradient-cyan">before your competitors see it.</span>
        </h1>

        <p className="hero-animate mt-6 text-[clamp(16px,1.5vw,20px)] leading-[1.6] text-[#9ca3af] max-w-[600px] mx-auto">
          ProcurAlerts monitors municipal and state procurement sources across all 6 New England states &mdash; and delivers matched bids to your inbox daily.
        </p>

        <div className="hero-animate mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://app.procuralerts.com/register"
            className="bg-[#06b6d4] text-[#0d1117] font-semibold px-8 py-3.5 rounded-lg hover:bg-[#22d3ee] transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] text-base"
          >
            Start 14-day free trial
          </a>
          <a
            href="https://app.procuralerts.com/register"
            className="border border-white/20 text-white font-semibold px-8 py-3.5 rounded-lg hover:border-[#06b6d4] hover:text-[#06b6d4] transition-all duration-200 text-base"
          >
            Request Pro Access
          </a>
        </div>

        {/* Stats */}
        <div className="hero-animate mt-14 grid grid-cols-3 gap-8 max-w-[500px] mx-auto">
          <div className="text-center">
            <div className="text-[clamp(24px,3vw,36px)] font-bold text-white">Daily</div>
            <div className="text-xs font-mono uppercase tracking-[0.08em] text-[#9ca3af] mt-1">Bid digest</div>
          </div>
          <div className="text-center">
            <div className="text-[clamp(24px,3vw,36px)] font-bold text-white">6</div>
            <div className="text-xs font-mono uppercase tracking-[0.08em] text-[#9ca3af] mt-1">NE states covered</div>
          </div>
          <div className="text-center">
            <div className="text-[clamp(24px,3vw,36px)] font-bold text-white">14-day</div>
            <div className="text-xs font-mono uppercase tracking-[0.08em] text-[#9ca3af] mt-1">Free trial</div>
          </div>
        </div>
      </div>

      <div
        ref={scrollIndicatorRef}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 transition-opacity duration-300"
      >
        <div className="relative w-px h-10 bg-[#6b7280]/50 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-[#06b6d4] animate-scroll-down" />
        </div>
        <span className="text-[10px] font-mono uppercase tracking-[0.08em] text-[#9ca3af]">
          Scroll to explore
        </span>
      </div>
    </section>
  )
}
